import { useState } from "react"
import { DataTable } from "@/components/datatable/datatable"
import { DataTableSupabase } from "@/components/datatable/supadatatable"
import { Page, PageTitle } from "@/components/page"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Progress } from "@/components/ui/progress"
import { Combobox } from "@/components/ui/combobox"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Layers, Plus, Check, Loader2, AlertCircle } from "lucide-react"
import { countriesList, domainToLabel } from "@/lib/agents/search/countries"
import { supabase } from "@/lib/data"
import axios from "axios"
import * as turndown from "turndown"
import { createEmbeddings } from "../vectors/utils"
import { app } from "@/lib/app"
import { usePage } from "@/components/page/hooks"
import { useTableController } from "@/components/datatable/controller"
import { usePermissions } from "@/lib/hooks/usePermissions"

type Vector = Partial<Table<"vectors">>

interface Service {
  id: number
  status: string
  name: Record<string, string>
  description: Record<string, string>
  date_created: number
  date_updated: number
  hasLocation: true
  location: [lat: number, lon: number]
}

interface VectorData {
  id: number
  external_id: string
  updated_at: string
}

interface ImportError {
  serviceId: number
  type: "embedding" | "insert" | "delete"
  message: string
}

interface ImportProgress {
  step: "idle" | "fetching_vectors" | "fetching_services" | "comparing" | "deleting" | "processing" | "done"
  vectorsProgress: number
  servicesProgress: number
  compareProgress: number
  vectorsTotal: number
  servicesTotal: number
  toCreateCount: number
  toUpdateCount: number
  toDeleteCount: number
  deleteProgress: number
  deleteTotal: number
  processingProgress: number
  processingTotal: number
  insertedCount: number
  errors: ImportError[]
}

export const services = {
  title: "Services",
  description: "Manage Services.",
  route: "/docs/services",
  url: "/docs/services",
  icon: Layers,
  component,
  group: "docs",
  resource: "sources",
  action: "read",
} satisfies PageConfig

const columns: Columns<View<"services_grouped">> = {
  domain: { header: "Country", size: 800, cell: ({ row }) => domainToLabel[row.original.domain] || row.original.domain },
  count: { header: "Services", cell: DataTable.cellRender.number, size: 200 },
}

const initialProgress: ImportProgress = {
  step: "idle",
  vectorsProgress: 0,
  servicesProgress: 0,
  compareProgress: 0,
  vectorsTotal: 0,
  servicesTotal: 0,
  toCreateCount: 0,
  toUpdateCount: 0,
  toDeleteCount: 0,
  deleteProgress: 0,
  deleteTotal: 0,
  processingProgress: 0,
  processingTotal: 0,
  insertedCount: 0,
  errors: [],
}

function component() {
  const turndownService = new turndown.default()

  const { team } = usePage()
  const ctrl = useTableController()
  const { canCreate } = usePermissions()
  const [countryDialogOpen, setCountryDialogOpen] = useState(false)
  const [progressDialogOpen, setProgressDialogOpen] = useState(false)
  const [selectedCountry, setSelectedCountry] = useState<number | null>(null)
  const [progress, setProgress] = useState<ImportProgress>(initialProgress)


  const countryItems = countriesList.map(c => ({ value: c.value, label: c.label }))

  async function onAddCountry() {
    setSelectedCountry(null)
    setCountryDialogOpen(true)
  }

  async function handleCountryAccept() {
    if (!selectedCountry) return
    setCountryDialogOpen(false)
    setProgress(initialProgress)
    setProgressDialogOpen(true)
    await importServices(selectedCountry)
  }

  async function importServices(id: number) {
    const toCreate: Service[] = []
    const toUpdate: Service[] = []
    const toDelete: number[] = []
    const key = (await app.fetchAPIkeys(team.id)).openai

    // Step 1: Fetch vectorized services from Supabase
    setProgress(p => ({ ...p, step: "fetching_vectors" }))
    await new Promise(r => setTimeout(r, 100))

    const { data, error } = await supabase.from("vectors").select("id, external_id, updated_at").eq("locale", `services_${id}`)

    if (error) {
      console.error("Error fetching vectors:", error)
      setProgress(p => ({ ...p, step: "done", errors: [...p.errors, { serviceId: 0, type: "embedding", message: `Failed to fetch vectors: ${error.message}` }] }))
      return
    }

    const vectorsData: VectorData[] = data || []
    setProgress(p => ({ ...p, vectorsProgress: 100, vectorsTotal: vectorsData.length }))
    await new Promise(r => setTimeout(r, 300))

    // Step 2: Fetch services from external API
    setProgress(p => ({ ...p, step: "fetching_services" }))
    await new Promise(r => setTimeout(r, 100))

    let servicesFull: Service[] = []
    try {
      const r = await axios.get(`https://directus-qa-support.azurewebsites.net/services/${id}`)
      servicesFull = r.data
    } catch (err: any) {
      console.error("Error fetching services:", err)
      setProgress(p => ({ ...p, step: "done", errors: [...p.errors, { serviceId: 0, type: "embedding", message: `Failed to fetch services: ${err.message}` }] }))
      return
    }

    setProgress(p => ({ ...p, servicesProgress: 100, servicesTotal: servicesFull.length }))
    await new Promise(r => setTimeout(r, 300))

    // Step 3: Compare and categorize
    setProgress(p => ({ ...p, step: "comparing" }))
    await new Promise(r => setTimeout(r, 100))

    const vectorsMap = new Map<string, VectorData>()
    for (const v of vectorsData) {
      vectorsMap.set(v.external_id, v)
    }

    const serviceIdsSet = new Set<number>()
    for (const s of servicesFull) {
      serviceIdsSet.add(s.id)
    }

    for (const service of servicesFull) {
      const externalIdStr = String(service.id)
      const vectorData = vectorsMap.get(externalIdStr)

      if (!vectorData) {
        toCreate.push(service)
      } else {
        const vectorUpdatedAt = new Date(vectorData.updated_at).getTime()
        const serviceUpdatedAt = service.date_updated

        if (serviceUpdatedAt > vectorUpdatedAt) {
          toUpdate.push(service)
        }
      }
    }

    for (const v of vectorsData) {
      const externalId = parseInt(v.external_id, 10)
      if (!isNaN(externalId) && !serviceIdsSet.has(externalId)) {
        toDelete.push(v.id)
      }
    }

    setProgress(p => ({
      ...p,
      compareProgress: 100,
      toCreateCount: toCreate.length,
      toUpdateCount: toUpdate.length,
      toDeleteCount: toDelete.length,
    }))
    await new Promise(r => setTimeout(r, 300))

    console.log("=== Import Services Results ===")
    console.log("To Create:", toCreate)
    console.log("To Update:", toUpdate)
    console.log("To Delete:", toDelete)
    console.log(`Summary: ${toCreate.length} to create, ${toUpdate.length} to update, ${toDelete.length} to delete`)

    // Step 4: Delete obsolete vectors
    if (toDelete.length > 0) {
      setProgress(p => ({ ...p, step: "deleting", deleteTotal: toDelete.length, deleteProgress: 0 }))
      await new Promise(r => setTimeout(r, 100))

      for (let i = 0; i < toDelete.length; i++) {
        const { error: deleteError } = await supabase.from("vectors").delete().eq("id", toDelete[i])
        if (deleteError) {
          console.error(`Error deleting vector ${toDelete[i]}:`, deleteError)
          setProgress(p => ({ ...p, errors: [...p.errors, { serviceId: toDelete[i], type: "delete", message: deleteError.message }] }))
        }
        setProgress(p => ({ ...p, deleteProgress: Math.round(((i + 1) / toDelete.length) * 100) }))
      }
    }

    // Add toUpdate items to toCreate
    for (const s of toUpdate) {
      toCreate.push(s)
    }

    // Delete vectors for items that need updating
    if (toUpdate.length > 0) {
      const { error: updateDeleteError } = await supabase.from("vectors").delete().in("external_id", toUpdate.map(s => String(s.id))).eq("locale", `services_${id}`)
      if (updateDeleteError) {
        console.error("Error deleting vectors for update:", updateDeleteError)
        setProgress(p => ({ ...p, errors: [...p.errors, { serviceId: 0, type: "delete", message: `Failed to delete vectors for update: ${updateDeleteError.message}` }] }))
      }
    }

    // Step 5: Process services - create embeddings and insert in batches
    if (toCreate.length > 0 && key) {
      setProgress(p => ({ ...p, step: "processing", processingTotal: toCreate.length, processingProgress: 0, insertedCount: 0 }))
      await new Promise(r => setTimeout(r, 100))

      const BATCH_SIZE = 100
      let totalInserted = 0

      for (let i = 0; i < toCreate.length; i += BATCH_SIZE) {
        const batch = toCreate.slice(i, Math.min(i + BATCH_SIZE, toCreate.length))
        const batchVectors: Vector[] = []

        // Create embeddings for this batch
        for (const service of batch) {
          for (const langKey of Object.keys(service.description)) {
            service.date_updated ||= service.date_created
            const name = service.name[langKey] || ""
            const descriptionHtml = service.description[langKey] || ""
            const descriptionMarkdown = turndownService.turndown(descriptionHtml)
            const content = name ? `${name}\n\n${descriptionMarkdown}` : descriptionMarkdown
            const updated_at = new Date(service.date_updated).toISOString()

            if (!content.trim()) continue

            try {
              const embeds = await createEmbeddings(content, key)

              for (const embed of embeds) {
                batchVectors.push({
                  vector: embed.embedding as any,
                  name,
                  external_id: String(service.id),
                  updated_at,
                  locale: `services_${id}`,
                  content: embed.content,
                  tokens: embed.tokens,
                  isChunk: false,
                })
              }
            } catch (err: any) {
              console.error(`Error creating embedding for service ${service.id}:`, err)
              setProgress(p => ({ ...p, errors: [...p.errors, { serviceId: service.id, type: "embedding", message: err.message || String(err) }] }))
            }
          }
        }

        // Insert this batch into Supabase
        if (batchVectors.length > 0) {
          const { error: insertError } = await supabase.from("vectors").insert(batchVectors as any)
          if (insertError) {
            console.error("Error inserting vectors batch:", insertError)
            setProgress(p => ({ ...p, errors: [...p.errors, { serviceId: 0, type: "insert", message: `Batch insert failed: ${insertError.message}` }] }))
          } else {
            totalInserted += batchVectors.length
          }
        }

        const processed = Math.min(i + BATCH_SIZE, toCreate.length)
        setProgress(p => ({ ...p, processingProgress: Math.round((processed / toCreate.length) * 100), insertedCount: totalInserted }))
        await new Promise(r => setTimeout(r, 50))
      }

      console.log(`Inserted ${totalInserted} vectors into Supabase`)
    }

    setProgress(p => ({ ...p, step: "done" }))
    console.log("=== Import Complete ===")
    ctrl.reload()
  }

  const getStepLabel = (step: ImportProgress["step"]) => {
    switch (step) {
      case "idle": return "Preparing..."
      case "fetching_vectors": return "Fetching vectorized services from database..."
      case "fetching_services": return "Fetching services from external API..."
      case "comparing": return "Comparing and categorizing services..."
      case "deleting": return "Deleting obsolete vectors..."
      case "processing": return "Creating embeddings and inserting..."
      case "done": return progress.errors.length > 0 ? "Completed with errors" : "Complete!"
      default: return ""
    }
  }

  const getOverallProgress = () => {
    const { step, vectorsProgress, servicesProgress, compareProgress, deleteProgress, processingProgress } = progress
    if (step === "idle") return 0
    if (step === "fetching_vectors") return vectorsProgress * 0.15
    if (step === "fetching_services") return 15 + servicesProgress * 0.15
    if (step === "comparing") return 30 + compareProgress * 0.10
    if (step === "deleting") return 40 + deleteProgress * 0.10
    if (step === "processing") return 50 + processingProgress * 0.50
    if (step === "done") return 100
    return 0
  }

  const selectedCountryLabel = selectedCountry ? countriesList.find(c => c.value === selectedCountry)?.label : ""

  return <Page>
    <div className="h-full grid grid-rows-[auto_1fr] gap-4">
      <div className="flex">
        <PageTitle />
        <div className="grow"></div>
        {canCreate("sources") && <div>
          <Button className="rounded-lg" onClick={onAddCountry}><Plus className="h-4 w-4" />Add/Update Country</Button>
        </div>}
      </div>
      <DataTableSupabase
        table="services_grouped"
        columns={columns}
        sort={["count", "desc"]}
        searchableColumns={["domain"]}
        controller={ctrl}
      />

      {/* Country Selection Dialog */}
      <Dialog open={countryDialogOpen} onOpenChange={setCountryDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Select Country</DialogTitle>
            <DialogDescription>Choose a country to import services from.</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Combobox items={countryItems} value={selectedCountry} onChange={setSelectedCountry} placeholder="Search country..." />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCountryDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCountryAccept} disabled={!selectedCountry}>Import</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Progress Dialog */}
      <Dialog open={progressDialogOpen} onOpenChange={(open) => { if (progress.step === "done") setProgressDialogOpen(open) }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Importing Services</DialogTitle>
            <DialogDescription>Importing services for {selectedCountryLabel}</DialogDescription>
          </DialogHeader>
          <div className="space-y-6 py-4">
            {/* Overall Progress */}
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium">{getStepLabel(progress.step)}</span>
                <span className="text-muted-foreground">{Math.round(getOverallProgress())}%</span>
              </div>
              <Progress value={getOverallProgress()} className="h-2" />
            </div>

            {/* Task 1: Fetch Vectors */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                {progress.vectorsProgress === 100 ? <Check className="h-4 w-4 text-green-500" /> : progress.step === "fetching_vectors" ? <Loader2 className="h-4 w-4 animate-spin text-blue-500" /> : <div className="h-4 w-4 rounded-full border-2 border-muted" />}
                <span className="text-sm">Fetch vectorized services from Supabase</span>
                {progress.vectorsTotal > 0 && <span className="text-xs text-muted-foreground ml-auto">{progress.vectorsTotal} records</span>}
              </div>
              <Progress value={progress.vectorsProgress} className="h-1.5" />
            </div>

            {/* Task 2: Fetch Services */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                {progress.servicesProgress === 100 ? <Check className="h-4 w-4 text-green-500" /> : progress.step === "fetching_services" ? <Loader2 className="h-4 w-4 animate-spin text-blue-500" /> : <div className="h-4 w-4 rounded-full border-2 border-muted" />}
                <span className="text-sm">Fetch services from external API</span>
                {progress.servicesTotal > 0 && <span className="text-xs text-muted-foreground ml-auto">{progress.servicesTotal} services</span>}
              </div>
              <Progress value={progress.servicesProgress} className="h-1.5" />
            </div>

            {/* Task 3: Compare */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                {progress.compareProgress === 100 ? <Check className="h-4 w-4 text-green-500" /> : progress.step === "comparing" ? <Loader2 className="h-4 w-4 animate-spin text-blue-500" /> : <div className="h-4 w-4 rounded-full border-2 border-muted" />}
                <span className="text-sm">Compare and categorize</span>
              </div>
              <Progress value={progress.compareProgress} className="h-1.5" />
            </div>

            {/* Task 4: Delete */}
            {(progress.deleteTotal > 0 || progress.step === "deleting") && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  {progress.deleteProgress === 100 ? <Check className="h-4 w-4 text-green-500" /> : progress.step === "deleting" ? <Loader2 className="h-4 w-4 animate-spin text-blue-500" /> : <div className="h-4 w-4 rounded-full border-2 border-muted" />}
                  <span className="text-sm">Delete obsolete vectors</span>
                  {progress.deleteTotal > 0 && <span className="text-xs text-muted-foreground ml-auto">{Math.round(progress.deleteProgress * progress.deleteTotal / 100)}/{progress.deleteTotal}</span>}
                </div>
                <Progress value={progress.deleteProgress} className="h-1.5" />
              </div>
            )}

            {/* Task 5: Process (Embeddings + Insert) */}
            {(progress.processingTotal > 0 || progress.step === "processing") && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  {progress.processingProgress === 100 ? <Check className="h-4 w-4 text-green-500" /> : progress.step === "processing" ? <Loader2 className="h-4 w-4 animate-spin text-blue-500" /> : <div className="h-4 w-4 rounded-full border-2 border-muted" />}
                  <span className="text-sm">Create embeddings & insert</span>
                  {progress.processingTotal > 0 && <span className="text-xs text-muted-foreground ml-auto">{Math.round(progress.processingProgress * progress.processingTotal / 100)}/{progress.processingTotal} services ({progress.insertedCount} vectors)</span>}
                </div>
                <Progress value={progress.processingProgress} className="h-1.5" />
              </div>
            )}

            {/* Results Summary */}
            {progress.compareProgress === 100 && (
              <div className="rounded-lg border bg-muted/50 p-4 space-y-2">
                <h4 className="font-medium text-sm">Results Summary</h4>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-bold text-green-600">{progress.toCreateCount}</div>
                    <div className="text-xs text-muted-foreground">To Create</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-blue-600">{progress.toUpdateCount}</div>
                    <div className="text-xs text-muted-foreground">To Update</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-red-600">{progress.toDeleteCount}</div>
                    <div className="text-xs text-muted-foreground">To Delete</div>
                  </div>
                </div>
              </div>
            )}

            {/* Errors Section */}
            {progress.errors.length > 0 && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-4 space-y-2">
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-red-500" />
                  <h4 className="font-medium text-sm text-red-700">Errors ({progress.errors.length})</h4>
                </div>
                <ScrollArea className="h-[100px]">
                  <div className="space-y-1">
                    {progress.errors.map((err, idx) => (
                      <div key={idx} className="text-xs text-red-600">
                        {err.serviceId > 0 ? `Service ${err.serviceId}: ` : ""}{err.type}: {err.message}
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button onClick={() => setProgressDialogOpen(false)} disabled={progress.step !== "done"}>
              {progress.step === "done" ? "Close" : "Processing..."}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  </Page>
}
